import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, Info, Link2, Upload } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Pastille } from '../ui/Primitives';
import { instantLisible, jourLisible } from '../veille/format';
import {
  urlFichierPreuve,
  type ControleQualiopi,
  type IndicateurQualiopi,
  type LienQualiopi,
  type PreuveQualiopi,
} from '../../lib/useQualiopi';

/**
 * Fiche d'un indicateur Qualiopi (TF-431) : son énoncé, ce que la plateforme
 * en contrôle, les preuves qu'il attend et celles qui y sont rattachées, avec
 * leur document source. Tout vient de l'API ; la fiche ne déduit rien.
 */

const ETATS_CONTROLE: Record<ControleQualiopi['etat'], { libelle: string; ton: string; icone: ReactNode }> = {
  SATISFAIT: {
    libelle: 'Satisfait',
    ton: 'success',
    icone: <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />,
  },
  MANQUANT: {
    libelle: 'À compléter',
    ton: 'warning',
    icone: <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden />,
  },
  INFORMATION: {
    libelle: 'Information',
    ton: 'neutral',
    icone: <Info className="h-5 w-5 text-codialis-gray" aria-hidden />,
  },
};

const CLASSE_LIEN = 'font-semibold text-codialis-sky hover:underline';

/** Un écran de la plateforme, une page extérieure, ou un simple texte quand l'écran est fermé au lecteur. */
export function LienSource({ lien, children }: { lien: LienQualiopi; children?: ReactNode }): JSX.Element {
  const contenu = children ?? lien.libelle;
  if (!lien.chemin) return <span>{contenu}</span>;
  if (lien.externe) {
    return (
      <a href={lien.chemin} target="_blank" rel="noreferrer noopener" className={`${CLASSE_LIEN} inline-flex items-center gap-1`}>
        {contenu}
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  return (
    <Link to={lien.chemin} className={CLASSE_LIEN}>
      {contenu}
    </Link>
  );
}

function Section({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{titre}</h3>
      {children}
    </section>
  );
}

function Controle({ controle }: { controle: ControleQualiopi }): JSX.Element {
  const etat = ETATS_CONTROLE[controle.etat];
  return (
    <div className="rounded-xl border border-gray-100 p-3 sm:p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{etat.icone}</span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 text-sm font-semibold text-codialis-navy">{controle.titre}</div>
            <Pastille texte={etat.libelle} ton={etat.ton} />
          </div>
          <p className="text-sm text-codialis-gray">{controle.constat}</p>
          {controle.manques.length > 0 && (
            <ul className="space-y-1 text-sm text-codialis-navy">
              {controle.manques.map((manque) => (
                <li key={manque.libelle} className="flex gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span className="min-w-0 break-words">
                    {manque.lien?.chemin ? <LienSource lien={manque.lien}>{manque.libelle}</LienSource> : manque.libelle}
                  </span>
                </li>
              ))}
              {controle.autresManques > 0 && (
                <li className="pl-3.5 text-xs text-codialis-gray">
                  et {controle.autresManques} autre{controle.autresManques > 1 ? 's' : ''}
                </li>
              )}
            </ul>
          )}
          {controle.sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-codialis-gray">
              <span>Source :</span>
              {controle.sources.map((source) => (
                <LienSource key={`${source.libelle}-${source.chemin ?? ''}`} lien={source} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function taille(octets: number | null): string {
  if (octets === null) return '';
  if (octets < 1024 * 1024) return `${Math.max(1, Math.round(octets / 1024))} Ko`;
  return `${(octets / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

function LignePreuve({
  preuve,
  numero,
  peutAgir,
  onRattacher,
  onRetirer,
}: {
  preuve: PreuveQualiopi;
  numero: number;
  peutAgir: boolean;
  onRattacher: () => void;
  onRetirer: () => void;
}): JSX.Element {
  const ici = preuve.rattachements.find((lien) => lien.indicateur === numero);
  const ailleurs = preuve.rattachements.filter((lien) => lien.indicateur !== numero).map((lien) => lien.indicateur);
  const retiree = preuve.etat === 'RETIREE';
  return (
    <li className="rounded-xl border border-gray-100 p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-codialis-sand text-codialis-navy sm:flex">
          {preuve.support === 'FICHIER' ? <FileText className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`min-w-0 break-words text-sm font-semibold text-codialis-navy ${retiree ? 'line-through' : ''}`}>
              {preuve.titre}
            </span>
            <Pastille texte={preuve.etatLibelle} ton={preuve.ton} />
          </div>
          <div className="text-xs text-codialis-gray">
            {ici?.attenduLibelle ?? 'Preuve complémentaire'} · document du {jourLisible(preuve.dateDocument)}
            {preuve.valableJusquAu && ` · valable jusqu’au ${jourLisible(preuve.valableJusquAu)}`} ·{' '}
            {preuve.site ? `Antenne ${preuve.site.code}` : 'Tout l’organisme'}
          </div>
          <div className="text-xs text-codialis-gray">
            Déposée le {instantLisible(preuve.deposeLe)}
            {preuve.deposePar && ` par ${preuve.deposePar}`}
            {ailleurs.length > 0 && ` · vaut aussi pour l’indicateur${ailleurs.length > 1 ? 's' : ''} ${ailleurs.join(', ')}`}
          </div>
          {preuve.description && <p className="text-xs text-codialis-navy">{preuve.description}</p>}
          {preuve.retrait && (
            <p className="text-xs text-codialis-gray">
              Retirée le {instantLisible(preuve.retrait.le)}
              {preuve.retrait.par && ` par ${preuve.retrait.par}`} : {preuve.retrait.motif}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          {preuve.support === 'FICHIER' ? (
            <a
              href={urlFichierPreuve(preuve.id)}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-gray-200 px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              Ouvrir{preuve.tailleOctets !== null ? ` · ${taille(preuve.tailleOctets)}` : ''}
            </a>
          ) : (
            preuve.url && (
              <a
                href={preuve.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-gray-200 px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Ouvrir la page
              </a>
            )
          )}
          {peutAgir && !retiree && (
            <>
              <button
                type="button"
                onClick={onRattacher}
                className="inline-flex min-h-[44px] items-center rounded-xl px-3 text-xs font-semibold text-codialis-sky hover:underline"
              >
                Rattachements
              </button>
              <button
                type="button"
                onClick={onRetirer}
                className="inline-flex min-h-[44px] items-center rounded-xl px-3 text-xs font-semibold text-red-600 hover:underline"
              >
                Retirer
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

export function FicheIndicateur({
  indicateur,
  preuves,
  peutDeposer,
  peutDeclarer,
  onDeposer,
  onRattacher,
  onRetirer,
  onApplicabilite,
  onFermer,
}: {
  indicateur: IndicateurQualiopi;
  /** Preuves rattachées à l'indicateur, retirées comprises. */
  preuves: PreuveQualiopi[];
  peutDeposer: boolean;
  peutDeclarer: boolean;
  onDeposer: (attendu: string | null) => void;
  onRattacher: (preuve: PreuveQualiopi) => void;
  onRetirer: (preuve: PreuveQualiopi) => void;
  onApplicabilite: () => void;
  onFermer: () => void;
}): JSX.Element {
  const actives = preuves.filter((preuve) => preuve.etat !== 'RETIREE');
  const retirees = preuves.filter((preuve) => preuve.etat === 'RETIREE');

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <Pastille texte={indicateur.statutLibelle} ton={indicateur.ton} />
        {indicateur.portee.conditionnelle && (
          <span className="text-xs text-codialis-gray">Portée : {indicateur.portee.libelle}</span>
        )}
      </div>

      <blockquote className="rounded-xl bg-codialis-sand p-4 text-sm leading-relaxed text-codialis-navy">
        {indicateur.enonce}
      </blockquote>

      {indicateur.observation && (
        <p className="flex items-start gap-2 text-xs text-codialis-gray">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0">{indicateur.observation}</span>
        </p>
      )}

      {indicateur.nonApplicable && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-codialis-navy">
          <div className="font-semibold">Déclaré non applicable</div>
          <p className="mt-1">{indicateur.nonApplicable.motif}</p>
          <p className="mt-1 text-xs text-codialis-gray">
            Le {instantLisible(indicateur.nonApplicable.declareLe)}
            {indicateur.nonApplicable.declarePar && ` par ${indicateur.nonApplicable.declarePar}`}. Ses exigences restent
            visibles et ne comptent pas.
          </p>
        </div>
      )}

      {indicateur.statut === 'A_COMPLETER' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-800">Ce qui manque</div>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {indicateur.manques.map((manque) => (
              <li key={manque} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                <span className="min-w-0 break-words">{manque}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {indicateur.controles.length > 0 && (
        <Section titre="Ce que la plateforme contrôle">
          <div className="space-y-2">
            {indicateur.controles.map((controle) => (
              <Controle key={controle.code} controle={controle} />
            ))}
          </div>
        </Section>
      )}

      {indicateur.exigences.length > 0 && (
        <Section titre="Preuves à déposer">
          <ul className="space-y-2">
            {indicateur.exigences.map((exigence) => (
              <li key={exigence.code} className="rounded-xl border border-gray-100 p-3 sm:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-codialis-navy">{exigence.libelle}</span>
                      <Pastille texte={exigence.remplie ? 'Déposée' : 'À compléter'} ton={exigence.remplie ? 'success' : 'warning'} />
                    </div>
                    <p className="text-xs text-codialis-gray">{exigence.aide}</p>
                    {exigence.manque && <p className="text-xs font-semibold text-amber-700">{exigence.manque}</p>}
                  </div>
                  {peutDeposer && (
                    <button
                      type="button"
                      onClick={() => onDeposer(exigence.code)}
                      className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                    >
                      <Upload className="h-3.5 w-3.5" aria-hidden />
                      Déposer
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section titre={`Preuves rattachées (${actives.length})`}>
        {actives.length === 0 ? (
          <p className="text-sm text-codialis-gray">
            Aucune preuve déposée pour cet indicateur
            {indicateur.controles.length > 0 ? ' : ce que la plateforme contrôle se lit sur ses écrans, ci-dessus.' : '.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {actives.map((preuve) => (
              <LignePreuve
                key={preuve.id}
                preuve={preuve}
                numero={indicateur.numero}
                peutAgir={peutDeposer}
                onRattacher={() => onRattacher(preuve)}
                onRetirer={() => onRetirer(preuve)}
              />
            ))}
          </ul>
        )}
        {retirees.length > 0 && (
          <details className="rounded-xl border border-gray-100 p-3">
            <summary className="min-h-[44px] cursor-pointer content-center text-xs font-semibold text-codialis-gray">
              {retirees.length} preuve{retirees.length > 1 ? 's' : ''} retirée{retirees.length > 1 ? 's' : ''}
            </summary>
            <ul className="mt-2 space-y-2">
              {retirees.map((preuve) => (
                <LignePreuve
                  key={preuve.id}
                  preuve={preuve}
                  numero={indicateur.numero}
                  peutAgir={false}
                  onRattacher={() => undefined}
                  onRetirer={() => undefined}
                />
              ))}
            </ul>
          </details>
        )}
      </Section>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:flex-wrap sm:justify-end">
        <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
        {peutDeclarer && indicateur.portee.conditionnelle && (
          <BoutonSecondaire onClick={onApplicabilite}>
            {indicateur.nonApplicable ? 'Rendre applicable' : 'Déclarer non applicable'}
          </BoutonSecondaire>
        )}
        {peutDeposer && <BoutonPrincipal onClick={() => onDeposer(null)}>Déposer une preuve</BoutonPrincipal>}
      </div>
    </div>
  );
}
